import { useEffect, useRef, useState } from "react";

/**
 * Custom hook to track an element's height dynamically using ResizeObserver
 *
 * @returns Object containing ref to attach to element and current height
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { ref, height } = useElementHeight();
 *   return (
 *     <>
 *       <div ref={ref}>Content that might resize</div>
 *       <div style={{ top: height }}>Positioned below</div>
 *     </>
 *   );
 * };
 * ```
 */
const useElementHeight = <TElement extends HTMLElement = HTMLDivElement>() => {
  const ref = useRef<TElement | null>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const updateHeight = () => {
      setHeight(element.offsetHeight);
    };

    // Initial measurement
    updateHeight();

    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return { ref, height };
};

export default useElementHeight;
